import React, { useMemo, useState, useRef } from "reactn";
import { Col, Row, Button, Input, Badge } from "reactstrap";
import { withRouter } from "react-router";
import { Query } from "react-apollo";
import { getCatalog } from "../../queries/Queries";
import { dateFormatter, datetimeFormatter } from "../../utils/Formatters";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import axios from "../../utils/Client";
import fileDownload from "js-file-download";
import Loading from "../../components/Loading/Loading";
import moment from "moment";
import _ from "lodash";
moment.locale("en");

const STATUS_OPTIONS = ["Active", "Inactive"];

const getFileNameFromUrn = (urn) =>
  urn ? urn.substring(urn.lastIndexOf(":") + 1) : "";

const getInitials = (fullName) => {
  if (!fullName) return "";
  const parts = fullName.trim().split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const DateRangeFilter = ({ label, startDate, endDate, onChange }) => (
  <div className='compact-date-filter'>
    <i className='icon ion-calendar compact-date-filter-icon' />
    <span className='compact-date-filter-label'>{label}</span>
    <input
      type='date'
      className='compact-date-filter-input'
      value={startDate ? startDate.format("YYYY-MM-DD") : ""}
      onChange={(e) =>
        onChange({
          startDate: e.target.value ? moment(e.target.value) : null,
          endDate,
        })
      }
    />
    <i className='icon ion-arrow-right-b compact-date-filter-arrow' />
    <input
      type='date'
      className='compact-date-filter-input'
      value={endDate ? endDate.format("YYYY-MM-DD") : ""}
      onChange={(e) =>
        onChange({
          startDate,
          endDate: e.target.value ? moment(e.target.value) : null,
        })
      }
    />
  </div>
);

const buildWhereClause = ({
  startDate,
  endDate,
  uploadedStartDate,
  uploadedEndDate,
  name,
  status,
}) => {
  const clauses = [];
  if (startDate && endDate) {
    clauses.push(
      `startDate >= "${startDate.format("YYYY-MM-DD")}" and endDate <= "${endDate.format("YYYY-MM-DD")}"`
    );
  }
  if (uploadedStartDate && uploadedEndDate) {
    clauses.push(
      `created >= '${uploadedStartDate.startOf("day").toISOString()}' and created <= '${uploadedEndDate.endOf("day").toISOString()}'`
    );
  }
  if (name) {
    clauses.push(`name==*${name}*`);
  }
  if (status) {
    clauses.push(`status==${status}`);
  }
  return clauses.length > 0 ? clauses.join(" and ") : null;
};

const CatalogPricesTableView = ({ history, setShowModal }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [uploadedStartDate, setUploadedStartDate] = useState(null);
  const [uploadedEndDate, setUploadedEndDate] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [uploadedFocusedInput, setUploadedFocusedInput] = useState(null);

  const debouncedSetNameFilter = useRef(
    _.debounce((value) => {
      setNameFilter(value);
      setPageNumber(1);
    }, 500)
  ).current;

  const handleClick = () => {
    setShowModal(true);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder }
  ) => {
    setPageNumber(page);
    setPageSize(sizePerPage);
    setSortField(sortField);
    setSortOrder(sortOrder);
  };

  const columns = useMemo(
    () => [
      {
        dataField: "name",
        text: "Upload Name",
        sort: true,
      },
      {
        dataField: "startDate",
        text: "Start Date",
        sort: true,
        formatter: (cell, row) => dateFormatter(row.startDate),
      },
      {
        dataField: "endDate",
        text: "End Date",
        sort: true,
        formatter: (cell, row) => dateFormatter(row.endDate),
      },
      {
        dataField: "status",
        text: "Status",
        sort: true,
        formatter: (cell, row) => (
          <Badge
            pill
            className={
              row.status === "Active"
                ? "status-pill status-pill-active"
                : "status-pill status-pill-inactive"
            }
          >
            {row.status}
          </Badge>
        ),
      },
      // TODO: uncomment once the backend Catalog type exposes uploadedAt
      {
        dataField: "created",
        text: "Uploaded At",
        sort: true,
        formatter: (cell, row) => datetimeFormatter(row.created),
      },
      // TODO: uncomment once the backend Catalog type exposes uploadedBy
      {
        dataField: "createdBy",
        text: "Uploaded By",
        sort: true,
        formatter: (cell, row) => (
          <span className='uploaded-by-cell'>
            <span className='uploaded-by-avatar'>
              {getInitials(row.createdBy)}
            </span>
            {row.createdBy}
          </span>
        ),
      },
      {
        dataField: "fileName",
        text: "File Name",
        sort: false,
        formatter: (cell, row) => (
          <span className='file-name-cell'>
            <i className='icon ion-document-text file-name-icon' />
            {getFileNameFromUrn(row.urn)}
          </span>
        ),
      },
      {
        dataField: "urn",
        text: "File",
        sort: true,
        formatter: (cell, row) => (
          <i
            className={"icon ion-android-download"}
            style={{ cursor: "pointer", color: "green", fontSize: "2em" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof REST_URL === "undefined") {
                var REST_URL = "";
              }
              axios
                .get(`${REST_URL}/cdn/cdn/resource/${row.urn}`, {
                  headers: {
                    Authorization: "Bearer " + localStorage.token,
                  },
                  responseType: "blob",
                })
                .then((response) => {
                  fileDownload(response.data, row.name + ".xlsx");
                })
                .catch((err) => console.error(err));
            }}
          />
        ),
      },
    ],
    [history]
  );

  return (
    <div className='organization catalog-prices-view'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Catalog Prices</h1>
        </Col>
      </Row>
      <div className='page-cnt'>
        <div className='catalog-filters-row'>
          <div className='catalog-filter-item'>
            <Input
              type='text'
              value={nameInput}
              placeholder='Upload name...'
              onChange={(e) => {
                setNameInput(e.target.value);
                debouncedSetNameFilter(e.target.value);
              }}
            />
          </div>
          <div className='catalog-filter-item'>
            <Input
              type='select'
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageNumber(1);
              }}
            >
              <option value=''>All statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Input>
          </div>
          <div className='catalog-filter-item'>
            <DateRangeFilter
              label='Period'
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
                setPageNumber(1);
              }}
            />
          </div>
          <div className='catalog-filter-item'>
            <DateRangeFilter
              label='Uploaded'
              startDate={uploadedStartDate}
              endDate={uploadedEndDate}
              onChange={({ startDate, endDate }) => {
                setUploadedStartDate(startDate);
                setUploadedEndDate(endDate);
                setPageNumber(1);
              }}
            />
          </div>
          <div className='catalog-filter-item catalog-filter-item-button'>
            <Button
              onClick={handleClick}
              className='btn btn-primary pull-right'
              color='primary'
            >
              {" "}
              <i className='icon ion-plus-round'></i>&nbsp;&nbsp; Upload Prices
            </Button>
          </div>
        </div>
        <Row>
          <Query
            query={getCatalog}
            variables={{
              pageNumber: pageNumber - 1,
              pageSize,
              sort: {
                "orders": [
                  {
                    "property": sortField,
                    "direction": sortOrder
                  }
                ],
            },
              where: buildWhereClause({
                startDate,
                endDate,
                uploadedStartDate,
                uploadedEndDate,
                name: nameFilter,
                status: statusFilter,
              }),
            }}
          >
            {({ loading, error, data }) => {
              if (loading) return <Loading />;
              if (error) return <p>Error</p>;

              if (!data || !Array.isArray(data.Catalog.content))
                return <p>No Data</p>;

              return (
                <React.Fragment>
                  <Col sm='12' className='mt-4'>
                    <BootstrapTable
                      classes='table-hover'
                      wrapperClasses='table-responsive'
                      bordered={false}
                      keyField='id'
                      data={[...data.Catalog.content]}
                      columns={columns}
                      remote={{
                        filter: true,
                        pagination: true,
                        sort: true,
                      }}
                      sort={{
                        dataField: sortField,
                        order: sortOrder,
                      }}
                      pagination={paginationFactory({
                        page: pageNumber,
                        sizePerPage: pageSize,
                        totalSize: data.Catalog.totalElements,
                        sizePerPageList: [
                          { text: "5", value: 5 },
                          { text: "10", value: 10 },
                          { text: "20", value: 20 },
                        ],
                      })}
                      onTableChange={handleTableChange}
                    />
                  </Col>
                </React.Fragment>
              );
            }}
          </Query>
        </Row>
      </div>
    </div>
  );
};

export default withRouter(CatalogPricesTableView);
